import { useState } from "react";
import { CircularProgress } from "@mui/material";

import PrimaryButton from "../../../Components/Buttons/primary-button";
import SelectField from "../../../Components/Form Fields/select-field";

import { useUpdateCardStatus } from "../../../CustomHooks/Mutations";

import { useModalContext } from "../../../Contexts/ModalContext";

import { dispatchErrorToast, dispatchSuccessToast } from "../../../Utils/helpers";
import { CARD_STATUS_REASONS, CARD_UNFREEZE_REASONS, modalVariants } from "../../../Utils/constants";

const COPY = {
  SUSPENDED: {
    title: "Freeze this card?",
    description: "Select a reason for freezing this card. All transactions will be blocked until you unfreeze it.",
    reasons: CARD_STATUS_REASONS,
    ctaLabel: "Freeze Card",
    successMessage: "Your card has been frozen.",
    errorMessage: "Unable to freeze card. Please try again.",
    ariaLabel: "Freeze card",
    modalTitle: "Freeze Card",
    toastKey: "freeze-card",
  },
  ACTIVE: {
    title: "Unfreeze this card?",
    description: "Select a reason for unfreezing this card. Transactions will be allowed again once it is active.",
    reasons: CARD_UNFREEZE_REASONS,
    ctaLabel: "Unfreeze Card",
    successMessage: "Your card has been unfrozen.",
    errorMessage: "Unable to unfreeze card. Please try again.",
    ariaLabel: "Unfreeze card",
    modalTitle: "Unfreeze Card",
    toastKey: "unfreeze-card",
  },
};

export default function renderCardStatusReason(modalContext, props) {
  const { updateModalComponent, setModalVariant, setModalTitle, setModalClassName, setModalAriaLabel } = modalContext;

  const copy = COPY[props?.status] ?? COPY.SUSPENDED;

  updateModalComponent(() => <CardStatusReasonModal {...props} />);
  setModalVariant(modalVariants.flexDialog);
  setModalTitle(copy.modalTitle);
  setModalAriaLabel?.(copy.ariaLabel);
  setModalClassName("");
}

function CardStatusReasonModal({ cardId, status = "SUSPENDED", onChanged }) {
  const { onModalClose } = useModalContext();

  const copy = COPY[status] ?? COPY.SUSPENDED;

  const [reason, setReason] = useState("");

  const { isLoading, mutate: updateCardStatus } = useUpdateCardStatus({
    onSuccess: () => {
      dispatchSuccessToast(copy.successMessage, { key: `${copy.toastKey}-${cardId}` });
      onModalClose();
      onChanged && onChanged();
    },
    onError: (e) =>
      dispatchErrorToast(e?.response?.data?.error || copy.errorMessage, {
        key: `${copy.toastKey}-${cardId}`,
      }),
  });

  return (
    <div className="modal-dialog-content">
      <h3 className="modal-status-title mb-2">{copy.title}</h3>
      <span className="modal-content-text mb-4">{copy.description}</span>
      <SelectField
        className="filled w-100 mb-4"
        label="Reason"
        options={copy.reasons}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        displayEmpty
        renderValue={(selected) => copy.reasons.find((option) => option.value === selected)?.label || "Select a reason"}
      />
      <div className="action-container split-equally">
        <PrimaryButton onClick={() => updateCardStatus({ status, reason, cardId })} disabled={!reason || isLoading}>
          {copy.ctaLabel} {isLoading && <CircularProgress />}
        </PrimaryButton>
        <PrimaryButton className="secondary-colors" onClick={() => onModalClose()}>
          Cancel
        </PrimaryButton>
      </div>
    </div>
  );
}
